/**
 * RoomReport — THE league report. One page, every source.
 *
 * 🔴 WHY THIS FILE EXISTS (Sep 2 2026). There were two league reports. A
 * Sleeper league got ranked, expandable team cards with a best buy, a worst
 * overpay, the room's top sales and a market headline; a pasted or
 * hand-entered room got a thinner list with none of that. Ben walked
 * the pasted flow and the verdict was that the two flows follow the exact same
 * presentation: the room's SOURCE may differ, the page must not. So the page is
 * here, once, and `roomSource.js` turns either provenance into the one shape it
 * draws.
 *
 * 🔴 WHAT IS ABSENT ON THE PASTE PATH, AND WHY. Absent, never defaulted, and
 * this list is the whole of it:
 *
 *   keeper banner   The keeper screen is a whole-room pass against the
 *                   benchmark top-60 that only the server report runs
 *                   (api/_lib/rateReport.js). A pasted room draws no banner,
 *                   which reads as "nobody looked", not "no keepers here".
 *                   NOT a data limit — a pasted room holds everything the
 *                   screen needs — so it is a gap worth closing, not a rule.
 *   share link      /rate?league=<id> re-fetches a public Sleeper league. A
 *                   pasted room was never stored anywhere, so there is no URL
 *                   that brings it back and the button is not offered.
 *   the room's own  A hand-entered room that is not fully priced has no market
 *   market headline to be measured against, so the headline falls back to the
 *                   room-relative sentence and the sales boards are gone with
 *                   it. A fully priced pasted room gets both, from the same
 *                   estimator the fetched one uses.
 *
 * 🔴 THE HEADLINE'S PERCENT IS SIZE-MATCHED OR ABSENT (Sep 3 2026). It measures
 * this room against real rooms of its OWN TEAM COUNT, and where the corpus
 * cannot back a room that size the page makes no room-level percentage at all —
 * it falls back to the room-relative headline and says why. Room size moves a
 * pooled comparison by 32 points end to end, so an unmatched percent was a
 * claim about how many seats the room had. roomSizeHeat.js holds the decision;
 * the per-player market columns keep the pooled basis and keep saying so.
 *
 * Everything else on this page — the ranked cards, the grades, the projected
 * points and rank, the position chips, the spend bar and its where-the-money-
 * went table, the build chip and the arithmetic behind it, the best buy, the
 * worst overpay, the expanded position-group roster, the top-five steals and
 * overpays boards, and the per-team click-through — renders identically from
 * both.
 *
 * 🔴 THE BUILD CHIP NAMES A SHAPE AND STOPS THERE. It says what a team's money
 * did and never how that build performs — no rate of anything, no placement, no
 * claim about past seasons. That layer is withheld on purpose, and the silence
 * covers comments too, which is why this paragraph describes the forbidden
 * claims rather than quoting one: buildArchetype.test.js greps this file and
 * the grep has no exemptions. buildArchetype.js's header holds the decision.
 *
 * 🔴 ONE PAGE, ONE YARDSTICK. Every badge here is the ROOM rank word or the
 * room letter — strong / solid / thin, and a surplus rank inside this auction —
 * and carries no percentile. The corpus percentiles live on a SEAT's own report
 * (TeamReportView), which is one tap away and says so. Putting a corpus
 * percentile on this page would give one report two yardsticks and no way for a
 * reader to tell which claim was which.
 *
 * A card the room does not rank — FLEX, SUPERFLEX, K, DEF and every bench card
 * — shows its players, its points and a dash where the badge would be. That is
 * the same rule the team tab uses for a group with no cell: never a word the
 * data cannot back.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { theme } from "../../data/theme.js";
import { starterBenchSpend, teamSpendBreakdown } from "../../utils/leagueSpend.js";
import { C, SZ, card, fineText, leagueNameText } from "../Submit/submitStyles.js";
import BuildChip, { wearsBuildChip } from "./BuildChip.jsx";
import { GRADE_COLOR, money, signedMoney } from "./GroupCard.jsx";
import RoomRoster, { RoomPositionChips } from "./RoomRoster.jsx";
import { cardEnter, expandEnter, gradePop, prefersReducedMotion } from "./rateMotion.js";
import { SpendBar, SpendDetail, StarterSplitLine } from "./SpendBreakdown.jsx";
import { ordinal } from "../../utils/ordinal.js";
import { captureRateReportViewed, captureRateTeamOpened } from "../../utils/rateAnalytics.js";

/** The room header's own buttons: quiet, secondary, all the same weight. */
export const roomNavBtn = {
  padding: "10px 16px",
  fontSize: SZ.small,
  fontWeight: 700,
  borderRadius: theme.radius.lg,
  border: `1px solid ${C.border.default}`,
  background: "transparent",
  color: C.text.secondary,
  cursor: "pointer",
};

const smallBtn = {
  padding: "6px 10px",
  fontSize: SZ.micro,
  fontWeight: 700,
  borderRadius: theme.radius.lg,
  border: `1px solid ${C.border.default}`,
  background: "transparent",
  color: C.text.secondary,
  cursor: "pointer",
};

/**
 * One superlative board: the room's five biggest steals, or its five worst
 * overpays.
 *
 * 🔴 IT SHOWS WHAT EXISTS AND NEVER PADS. A steal has to be a steal and an
 * overpay an overpay, so a room where everything cleared near the market gets a
 * shorter board — or, with nothing qualifying at all, no board. A fifth row
 * reading "+$0" would be the page inventing a sale to fill a shape.
 *
 * The numbers are the same three the card headlines above already speak in:
 * paid, market, and the gap between them, in the league's own dollars. Nothing
 * here is a percentile and nothing here is a percentage — a $2 flier bought for
 * $1 is a 50% steal and nobody's story.
 */
function SalesBoard({ one, many, sales, testId, positive }) {
  if (!sales || sales.length === 0) return null;
  const gapColor = positive ? C.status.success : C.status.danger;
  // 🔴 THE HEADING COUNTS THE ROWS. "Top 5 steals" over three rows is the same
  // padding the board itself refuses, done in words — and a room with only
  // three sales the market undercut is a real and interesting room, not a
  // broken one.
  const n = sales.length;
  return (
    <div data-testid={testId} style={{ flex: "1 1 260px", minWidth: 0 }}>
      <div style={{ fontSize: SZ.small, fontWeight: 800, color: C.text.secondary, marginBottom: 4 }}>
        Top {n} {n === 1 ? one : many}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: SZ.small }}>
        <tbody>
          {sales.map((sale, i) => (
            <tr key={`${sale.player}-${sale.team}`} data-testid={`${testId}-${i + 1}`}>
              <td
                style={{
                  padding: "3px 0",
                  color: C.text.faint,
                  width: 14,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {i + 1}
              </td>
              <td style={{ padding: "3px 6px 3px 0", minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontWeight: 700,
                    color: C.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sale.player}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: SZ.micro,
                    color: C.text.faint,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sale.team}
                </span>
              </td>
              <td
                style={{
                  padding: "3px 0",
                  textAlign: "right",
                  color: C.text.secondary,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {money(sale.paid)} / {money(sale.bench)}
              </td>
              <td
                style={{
                  padding: "3px 0 3px 8px",
                  textAlign: "right",
                  fontWeight: 800,
                  color: gapColor,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {signedMoney(sale.surplus)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The market headline, with a capital on the front.
 *
 * 🔴 THE CAPITAL IS A RENDER FACT, NOT A GENERATOR FACT. roomSizeHeat.js and
 * the room-relative fallback both emit lowercase sentence fragments, and
 * roomSizeContext.test.js pins those exact strings — deliberately, because a
 * fragment is composable and a sentence is not. So the page, which is the one
 * place the fragment becomes a sentence, is the one place the capital is
 * added. It touches the first character and nothing else: a headline naming a
 * league or a position keeps every other letter it arrived with.
 */
function capFirst(s) {
  return typeof s === "string" && s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s;
}

/**
 * One team, on either path.
 *
 * The testid prefix comes from the source rather than being fixed here, because
 * the two paths key their cards on different things — a Sleeper roster id, a
 * pasted seat's manager name — and both are already pinned by tests that are
 * about behaviour rather than about which page drew the card.
 */
function RoomTeamCard({ card: t, prefix, cardPrefix, index, ranks, teamCount, wide, roomShares, benchmarkFormat, budget, avgSurplus, onOpen }) {
  const [open, setOpen] = useState(false);
  // Read once per render, exactly as CorpusMeter reads it. The entrance is a CSS
  // animation whose string depends only on `index` and this flag, so opening the
  // picks below re-renders the card without replaying its arrival.
  const still = prefersReducedMotion();
  const showProj = !!t.proj;
  // 🔴 A ROSTER TYPED WITHOUT DOLLARS GETS NO BAR AND NO DETAIL, rather than a
  // bar made of zeroes. hasPrices is passed explicitly even though the totals
  // would reach the same answer: this is the page's absent-not-defaulted rule
  // and it should be legible at the call site.
  const spend = useMemo(
    () => teamSpendBreakdown({ picks: t.picks, roomShares, benchmarkFormat, budget, hasPrices: t.hasPrices }),
    [t.picks, t.hasPrices, roomShares, benchmarkFormat, budget]
  );
  // Same three fields on both sources, so the line is the same line on both.
  // Null on an unpriced roster, on a league that sent no scoring, and on a
  // roster the projection set matched nothing in — leagueSpend.js says why.
  const split = useMemo(
    () => starterBenchSpend({ picks: t.picks, proj: t.proj, hasPrices: t.hasPrices }),
    [t.picks, t.proj, t.hasPrices]
  );

  return (
    <div
      data-testid={`${cardPrefix}-${t.key}`}
      style={{ ...card, padding: 14, marginBottom: 10, ...cardEnter(index, still) }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          data-testid={`${prefix}-grade-${t.key}`}
          style={{
            width: 44,
            height: 44,
            borderRadius: theme.radius.lg,
            background: C.bg.deep,
            border: `1px solid ${C.border.default}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: t.badgeIsGrade ? 18 : 14,
            fontWeight: 900,
            color: t.badgeIsGrade ? GRADE_COLOR(t.grade) : C.text.faint,
            flexShrink: 0,
            ...gradePop(index, still),
          }}
        >
          {t.badge}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: SZ.body,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t.name}
          </div>
          {/* The build chip sits with the name because it is a fact about this
              team, not a measurement against the room like the two lines under
              it. The evidence for it is one tap away in the expansion. */}
          {/* The wrapper asks the chip whether there IS a chip, rather than
              testing the build itself: Balanced draws nothing (BuildChip.jsx
              carries why), and an empty line of its own is still a line. */}
          {wearsBuildChip(t.build) && (
            <div style={{ marginTop: 2 }}>
              <BuildChip build={t.build} testId={`${prefix}-build-${t.key}`} />
            </div>
          )}
          {t.hasPrices && (
            <div style={{ fontSize: SZ.small, color: C.text.secondary }}>
              market surplus {signedMoney(t.surplus)} on {money(t.spent)} spent
              {/* 🔴 THE REFERENCE MARK THAT MAKES A GREEN NUMBER READABLE. A
                  +$26 beside a C is not a contradiction once the room's own
                  average is +$29 beside it — the letter ranks this seat inside
                  the room and the dollars are measured against the pooled
                  market, and those are different questions. Absent, never
                  zeroed, on a room with nothing to average. */}
              {avgSurplus != null && (
                <span data-testid={`${prefix}-room-avg-${t.key}`}>
                  , room average {signedMoney(avgSurplus)}
                </span>
              )}
            </div>
          )}
          {showProj && (
            <div style={{ fontSize: SZ.small, color: C.text.secondary }}>
              projected week 1 lineup:{" "}
              <b style={{ color: C.text.primary }}>{(t.proj.week1 || 0).toFixed(1)}</b> (
              {ordinal(t.proj.rank)} of {teamCount})
            </div>
          )}
        </div>
      </div>
      <SpendBar breakdown={spend} testId={`${prefix}-spend-bar-${t.key}`} />
      {showProj && <RoomPositionChips grades={t.proj.positionGrades} />}
      {(t.bestBuy || t.worstOverpay) && (
        <div style={{ marginTop: 8, fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}>
          {t.bestBuy && (
            <span>
              Best buy: <b style={{ color: C.text.primary }}>{t.bestBuy.player}</b> at{" "}
              {money(t.bestBuy.paid)}, market {money(t.bestBuy.bench)}.
            </span>
          )}{" "}
          {t.worstOverpay && (
            <span>
              Worst overpay: <b style={{ color: C.text.primary }}>{t.worstOverpay.player}</b> at{" "}
              {money(t.worstOverpay.paid)}, market {money(t.worstOverpay.bench)}.
            </span>
          )}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        <button onClick={() => setOpen(!open)} style={smallBtn}>
          {open ? "Hide picks" : `Show all ${t.picks.length} picks`}
        </button>
        {/* 🔴 EVERY SEAT IS A DOOR INTO THE SINGLE-TEAM REPORT, ON BOTH PATHS.
            Ben, Sep 1 2026: "Can we be able to click into each team to access
            that page from that team's perspective. This makes more sense than
            having to input each one independently through Rate my team." The
            room view answers "who won"; the seat report answers "what did THIS
            manager do", with the corpus percentiles, the graded position cards
            and the best-buy and worst-overpay in full. Both already existed.
            Until now the pasted room was the only one with a door, and the
            fetched Sleeper league had none at all. */}
        {onOpen && (
          <button
            onClick={onOpen}
            data-testid={`${prefix}-open-${t.key}`}
            style={{ ...smallBtn, fontWeight: 800, border: `1px solid ${C.border.brand}`, color: C.brand.accent }}
          >
            Rate this team
          </button>
        )}
      </div>
      {open && (
        <div data-testid={`${prefix}-picks-${t.key}`} style={expandEnter(still)}>
          {!showProj && (
            <div style={{ marginTop: 8, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>
              No scoring settings came with this league, so no lineup was filled and these cards are
              the roster rather than a starters and bench split.
            </div>
          )}
          {/* 🔴 THE CHIP'S OWN ARITHMETIC, SO THE LABEL IS CHECKABLE. It names
              the exact quantities the matched rule read, in the league's own
              budget, and says nothing about how the build tends to do — that
              layer is deliberately absent (buildArchetype.js's header carries
              the decision). */}
          {t.build && t.build.evidence && (
            <div
              data-testid={`${prefix}-build-why-${t.key}`}
              style={{ marginTop: 8, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}
            >
              <b style={{ color: C.text.secondary }}>{t.build.label}.</b> {t.build.evidence}
            </div>
          )}
          {/* 🔴 WHERE THE MONEY WENT, THE OTHER WAY OF CUTTING IT. The table
              below splits by position; this splits by whether the player
              actually starts, which is the cut a reader makes when they ask
              whether a roster is top-heavy. The percentages are of what the
              team SPENT, so they partition and sum to 100 — the bench figure
              is drawn as the complement of the starter one for exactly that
              reason, so two roundings can never add to 99 or 101.

              🔴 AND IT MAKES NO MARKET CLAIM. The corpus carries no
              starter/bench distribution, so there is no "vs real rooms" here
              and none may be added without a corpus recompute first. */}
          <StarterSplitLine
            split={split}
            testId={`${prefix}-starter-split-${t.key}`}
            style={{ marginTop: 10 }}
          />
          <SpendDetail breakdown={spend} testId={`${prefix}-spend-detail-${t.key}`} />
          {/* The SAME roster the seat report's own room view draws. */}
          <RoomRoster
            testId={`${prefix}-roster-${t.key}`}
            picks={t.picks}
            proj={t.proj}
            ranks={ranks}
            teamCount={teamCount}
            hasPrices={t.hasPrices}
            wide={wide}
          />
        </div>
      )}
    </div>
  );
}

/**
 * @param {object}   room       a roomSource shape: sleeperRoomSource or localRoomSource
 * @param {node}     subline    the fine line under the headline, the source's own words
 * @param {node}     actions    the buttons in the header card, the source's own
 * @param {node}     methodology the fine text under the header, the source's own
 * @param {node}     footnote   the fine text under the cards, the source's own
 * @param {function} onOpenTeam (seat) => void, or absent for no click-through
 * @param {node}     children   whatever the source puts after the report
 */
export default function RoomReport({
  room,
  subline,
  actions,
  methodology,
  footnote,
  onOpenTeam,
  /* 🔴 "paste" | "sleeper" | "manual" — the funnel's own word for how this room
     got here, which the room shape cannot supply: `room.source` is "local" for
     BOTH a pasted and a hand-typed room, and those are the two paths whose
     conversion is worth telling apart. Passed by the adapter that knows.
     Null-safe: a caller with no opinion records none rather than a guess. */
  viewSource = null,
  children,
}) {
  // Two columns is the phone budget, same rule and same breakpoint as the seat
  // report: a wide viewport carries the price trio inline, a phone puts it
  // behind the tap the row already has.
  const [wide, setWide] = useState(typeof window === "undefined" ? true : window.innerWidth > 820);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth > 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* 🔴 A REPORT OPENS AT THE TOP OF THE PAGE. The surfaces on /rate swap by
     state rather than by navigation, so the browser keeps whatever scroll
     offset the previous screen had — and the screens before a report are the
     tall ones (a long paste box, a fourteen-seat roster), so a reader who
     scrolled to press the button landed halfway down their own grade.

     MOUNT-SCOPED ON PURPOSE, and the empty dep array is the whole guard: this
     fires when the surface comes into view, never on a re-render. Typing in a
     field, toggling a chip or expanding a row must not yank the page upward.
     Every transition that should scroll is a real mount here — the room and
     the seat are mutually exclusive branches, so opening a team, coming back
     to the league, and swapping tabs all unmount one and mount the other.

     try/catch guards a HOST without scrollTo at all (a non-browser renderer),
     not jsdom: jsdom defines the function and LOGS "Not implemented" rather
     than throwing, so nothing here can catch that. Suite quiet comes from the
     stub in src/setupTests.js instead. */
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* no scrollTo (jsdom, or a non-browser host) — nothing to do */
    }
  }, []);

  /* 🔴 ONE VIEW EVENT, FIRED FROM THE ONE COMPONENT ALL THREE SOURCES RENDER.
     Putting it in the three adapters instead would let "viewed" come to mean a
     slightly different moment on each path, which is exactly the drift this
     component exists to prevent. Once per mount: walking out to a seat and back
     is a second look at the room, and counting it as one would understate the
     page people actually use. The ref guards StrictMode's double effect run,
     the same way the team report's own events do. */
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    captureRateReportViewed({ source: viewSource, teams: room.teams, format: room.format });
  }, [viewSource, room.teams, room.format]);

  return (
    <div data-testid="room-report" data-source={room.source}>
      <div style={{ ...card, marginBottom: 14 }}>
        <div data-testid="room-league-line" style={{ fontSize: SZ.small, color: C.text.secondary }}>
          {/* The NAME takes the accent; the fallback does not. "Your league" is
              not an identity, and colouring it would promise a reader their
              league was recognised when it was not. */}
          {room.leagueName ? (
            <span data-testid="room-league-name" style={leagueNameText}>
              {room.leagueName}
            </span>
          ) : (
            "Your league"
          )}{" "}
          · {room.teams} teams · ${room.budget} budget
        </div>
        {room.headline && (
          <div
            data-testid="room-headline"
            style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.3, margin: "6px 0 2px" }}
          >
            {capFirst(room.headline)}
          </div>
        )}
        {/* 🔴 A ROOM-LEVEL MARKET PERCENT IS ONLY EVER SIZE-MATCHED. The
            headline above is this room measured against real rooms ITS OWN
            SIZE, and this line shows the two pooled figures that came from —
            or, where the corpus cannot back a room this size, says so and the
            headline carries no percentage at all. Room size moves a pooled
            comparison by 32 points end to end; roomSizeHeat.js holds the
            measured cells and the decision. */}
        {room.sizeNote && (
          <div
            data-testid="room-size-context"
            style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5, marginBottom: 2 }}
          >
            {room.sizeNote}
          </div>
        )}
        {/* 🔴 THE ARITHMETIC A READER WAS BEING LEFT TO DO. Ben, on the
            deployed report: nine of ten teams show a positive surplus. They do,
            and it is not an error — the surpluses ARE the room's underpayment
            split ten ways, so a room that paid under the market has to hand out
            mostly green numbers. The sentence says that once, in figures taken
            off the cards below rather than off the heat, so a reader can add up
            the column and land on the number in the sentence.
            roomSource.js's roomSurplusContext holds the rule. */}
        {room.surplusNote && (
          <div
            data-testid="room-surplus-note"
            style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5, marginBottom: 2 }}
          >
            {room.surplusNote}
          </div>
        )}
        {subline && (
          <div style={{ fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>{subline}</div>
        )}
        {room.keeperFlag && (
          <div
            data-testid="keeper-banner"
            style={{
              marginTop: 10,
              padding: "8px 10px",
              borderRadius: theme.radius.lg,
              border: `1px solid ${C.status.warning}`,
              fontSize: SZ.small,
              color: C.status.warning,
              lineHeight: 1.4,
            }}
          >
            Keeper prices detected: grades treat keepers as bargains, read with that in mind
          </div>
        )}
        {actions && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>{actions}</div>
        )}
      </div>

      {/* 🔴 THE ROOM'S SALES, BEFORE THE ROOM'S TEAMS, AND THE ONLY HOME FOR A
          SUPERLATIVE. The header used to carry the single biggest steal and the
          single biggest fight as two sentences of their own; those were the
          first row of each board below, said twice, so they are gone and the
          boards are where a reader finds their own league in the page. */}
      {(room.steals || room.overpays) && (room.steals || []).concat(room.overpays || []).length > 0 && (
        <div
          data-testid="room-sales-boards"
          style={{ ...card, marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 18 }}
        >
          <SalesBoard
            one="steal"
            many="steals"
            sales={room.steals}
            testId="room-top-steals"
            positive
          />
          <SalesBoard
            one="overpay"
            many="overpays"
            sales={room.overpays}
            testId="room-top-overpays"
          />
        </div>
      )}

      {methodology && <div style={{ ...fineText, marginTop: 0, marginBottom: 10 }}>{methodology}</div>}

      {/* Rank order is the render order, so the stagger reads as a leaderboard
          counting down rather than as rows appearing at random. */}
      {room.cards.map((t, i) => (
        <RoomTeamCard
          key={t.key}
          card={t}
          prefix={room.testPrefix}
          cardPrefix={room.cardTestPrefix}
          index={i}
          ranks={room.ranks}
          teamCount={room.projTeamCount}
          wide={wide}
          roomShares={room.roomShares}
          benchmarkFormat={room.benchmarkFormat}
          budget={room.budget}
          avgSurplus={room.avgSurplus}
          /* The seat click is a funnel joint, so it is counted where it is
             wired rather than in each of the three callers. Source only: the
             room's size and format already arrived on the view event a moment
             ago, and the seat itself may never be named. */
          onOpen={
            onOpenTeam
              ? () => {
                  captureRateTeamOpened({ source: viewSource });
                  onOpenTeam(t.seat);
                }
              : null
          }
        />
      ))}

      {footnote && <div style={{ ...fineText, marginTop: 4, marginBottom: 16 }}>{footnote}</div>}

      {children}
    </div>
  );
}
